/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { WineClubDetails } from './components/wine-club-details';
import { IOrderDetailsViewProps } from './smwe-order-details';

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation & { isWineClubOrder?: boolean; props?: IOrderDetailsViewProps }> = ({
    orderInformationProps,
    salesId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    isWineClubOrder,
    props
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {channelReferenceId}
        {salesId}
        {createdDate}
        {count}
        {amount}
        {isWineClubOrder && props && WineClubDetailsRender(props)}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
    </Node>
);

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery & { isWineClubOrder?: boolean }> = ({ deliveryProps, heading, count, address, trackingInfo, isWineClubOrder }) => {
    return (
        <Node {...deliveryProps}>
            {heading}
            {count}
            {address}
            {trackingInfo}
        </Node>
    );
};

export const OrderDetailsGroup: React.FC<IGroup & { isWineClubOrder?: boolean }> = ({ groupProps, delivery, salesLinesProps, salesLines, isWineClubOrder }) => (
    <Node {...groupProps}>
        {delivery && <OrderDetailsGroupDelivery {...delivery} isWineClubOrder={isWineClubOrder} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        <OrderDetailsSalesLine {...salesLine} />
                        {salesLine.salesStatus}
                    </React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderDetailsGroups: React.FC<IGroups & { isWineClubOrder?: boolean; isWineClubPickup?: boolean }> = ({ groupsProps, groups, isWineClubOrder, isWineClubPickup }) => (
    <Node {...groupsProps} className={classnames(groupsProps.className, {'ms-order-details__wine-club-group': isWineClubOrder}, {'ms-order-details__wine-club-pickup': isWineClubPickup})}>
        {groups.map((group, index) => (
            <OrderDetailsGroup key={index} {...group} isWineClubOrder={isWineClubOrder} />
        ))}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount, earnedPoints }) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

export const WineClubDetailsRender = (props: IOrderDetailsViewProps) => {
    return <WineClubDetails orderDetailProps={props} wineClubData={props.wineClubData!} />;
};

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = (props) => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        isWineClubOrder,
        isWineClubPickup
    } = props;

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderDetailsOrderInfomation {...orderInfomation} isWineClubOrder={isWineClubOrder} props={props} />}
            {groups && <OrderDetailsGroups {...groups} isWineClubOrder={isWineClubOrder} isWineClubPickup={isWineClubPickup} />}
            {orderSummary && <OrderDetailsOrderSummary {...orderSummary} />}
            {payment && <OrderDetailsPayment {...payment} />}
            {help && <OrderDetailsHelp {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
